import React from 'react';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import { styled } from '@mui/material/styles';

const DefaultButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.t100,
  border: `1px solid ${theme.palette.primary.t100}`,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
}));

const AltDefaultButton = styled(Button)(({ theme }) => ({
  color: theme.palette.semantic.error,
  border: `1px solid ${theme.palette.semantic.error}`,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    border: `1px solid ${theme.palette.semantic.error}`,
    backgroundColor: theme.palette.semantic.error2,
  },
}));

const GhostButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.t70,
  border: `1px solid ${theme.palette.text.t50}`,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    border: `1px solid ${theme.palette.text.t70}`,
    color: theme.palette.text.t100,
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.t100,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.t100,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.semantic.error,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.semantic.error,
  },
}));

const ReportButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.t70,
  textTransform: 'none',
  '&:focus': {
    outline: 'none',
  },
}));

// cancel-colored default button
const AltDefault = ({
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) => (
  <AltDefaultButton
    onClick={onClick}
    disabled={disabled}
    variant='outlined'
    sx={sx}
    startIcon={startIcon}
    endIcon={endIcon}
    {...props}
  >
    {text}
  </AltDefaultButton>
);

const Default = ({
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) => (
  <DefaultButton
    onClick={onClick}
    disabled={disabled}
    variant='outlined'
    sx={sx}
    startIcon={startIcon}
    endIcon={endIcon}
    {...props}
  >
    {text}
  </DefaultButton>
);

const Ghost = ({
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) => (
  <GhostButton
    onClick={onClick}
    disabled={disabled}
    variant='outlined'
    sx={sx}
    startIcon={startIcon}
    endIcon={endIcon}
    {...props}
  >
    {text}
  </GhostButton>
);

const Cancel = ({
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) => (
  <CancelButton
    onClick={onClick}
    disabled={disabled}
    sx={sx}
    startIcon={startIcon}
    endIcon={endIcon}
    {...props}
  >
    {text}
  </CancelButton>
);
const Submit = ({ text, onClick, disabled, sx, ...props }) => (
  <SubmitButton
    onClick={onClick}
    disabled={disabled}
    variant='contained'
    sx={sx}
    {...props}
  >
    {text}
  </SubmitButton>
);

const Delete = ({ text, onClick, disabled, sx, ...props }) => (
  <DeleteButton
    onClick={onClick}
    disabled={disabled}
    variant='contained'
    sx={sx}
    {...props}
  >
    {text}
  </DeleteButton>
);

const Report = ({
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) => (
  <ReportButton
    onClick={onClick}
    disabled={disabled}
    sx={sx}
    startIcon={startIcon && <Icon>{startIcon}</Icon>}
    endIcon={endIcon && <Icon>{endIcon}</Icon>}
    {...props}
  >
    {text}
  </ReportButton>
);

const buttonTypes = {
  default: Default,
  altDefault: AltDefault,
  ghost: Ghost,
  cancel: Cancel,
  submit: Submit,
  delete: Delete,
  report: Report,
};

export default function MyButton({
  type,
  text,
  onClick,
  disabled,
  sx,
  startIcon,
  endIcon,
  ...props
}) {
  const BtnType = buttonTypes[type] || buttonTypes['default'];
  // some mui props may not work as intended, double check when testing

  return (
    <BtnType
      text={text}
      onClick={onClick}
      disabled={disabled}
      sx={sx}
      startIcon={startIcon}
      endIcon={endIcon}
      {...props}
    />
  );
}
